import React from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  ViewListOutlined,
  RepeatOutlined,
} from "@mui/icons-material";

export type ActivePanel = "backlog" | "recurring" | null;

interface CalendarPillRailProps {
  activePanel: ActivePanel;
  onToggle: (panel: "backlog" | "recurring") => void;
  backlogCount: number;
}

/**
 * Pill Rail — 36px vertical strip between sidebar and calendar.
 * Two stacked pills: Backlog (terracotta active) + Recurring (charcoal active).
 * Click toggles the corresponding panel; only one open at a time.
 */
const CalendarPillRail: React.FC<CalendarPillRailProps> = ({
  activePanel,
  onToggle,
  backlogCount,
}) => {
  const { translate } = useTranslation("operations");
  return (
    <div className="w-[36px] flex-shrink-0 bg-white border-r border-mid-border flex flex-col items-center pt-4 gap-3">
      {/* Backlog pill */}
      <button
        onClick={() => onToggle("backlog")}
        className={`relative flex flex-col items-center justify-center w-[28px] rounded-[6px] py-3 transition-all duration-200 ${
          activePanel === "backlog"
            ? "bg-mid-terracotta shadow-sm"
            : "bg-white border border-mid-border hover:shadow-sm hover:-translate-y-0.5"
        }`}
        title={translate("board.backlog")}
        aria-label={translate("calendar.toggleBacklog")}
      >
        <ViewListOutlined
          sx={{
            fontSize: 16,
            color: activePanel === "backlog" ? "#FFFFFF" : "#8C9099",
          }}
        />
        {/* Vertically stacked label */}
        <div className="flex flex-col items-center mt-1 gap-[1px]">
          {"BACKLOG".split("").map((ch, i) => (
            <span
              key={i}
              className={`text-[8px] font-semibold leading-none ${
                activePanel === "backlog" ? "text-white" : "text-mid-muted"
              }`}
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {ch}
            </span>
          ))}
        </div>

        {/* Count badge — always visible */}
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full text-[9px] font-bold text-white px-1"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            backgroundColor: "#D4572B",
          }}
        >
          {backlogCount > 99 ? "99+" : backlogCount}
        </span>
      </button>

      {/* Recurring pill */}
      <button
        onClick={() => onToggle("recurring")}
        className={`relative flex flex-col items-center justify-center w-[28px] rounded-[6px] py-3 transition-all duration-200 ${
          activePanel === "recurring"
            ? "bg-mid-charcoal shadow-sm"
            : "bg-white border border-mid-border hover:shadow-sm hover:-translate-y-0.5"
        }`}
        title={translate("calendar.pmSchedules")}
        aria-label={translate("calendar.toggleRecurring")}
      >
        <RepeatOutlined
          sx={{
            fontSize: 16,
            color: activePanel === "recurring" ? "#FFFFFF" : "#8C9099",
          }}
        />
        {/* Vertically stacked label */}
        <div className="flex flex-col items-center mt-1 gap-[1px]">
          {"PM".split("").map((ch, i) => (
            <span
              key={i}
              className={`text-[8px] font-semibold leading-none ${
                activePanel === "recurring" ? "text-white" : "text-mid-muted"
              }`}
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {ch}
            </span>
          ))}
        </div>
      </button>
    </div>
  );
};

export default CalendarPillRail;
